// filepath: apps/web/src/features/admin/secrets/queries.ts
//
// FR-UI-ADM-10 -- TanStack Query hooks for the secret reference
// admin editor.
//
// All hooks include `tenantSlug` in the cache key as the second
// segment (INV-1 / FR-UI-CORE-1 pattern). Switching tenants does
// NOT leak the prior tenant's secret list -- the cache key changes,
// the query refetches.
//
// Mutations carry `idempotency_key: crypto.randomUUID()` (INV-3 /
// FR-UI-PROM-2 pattern) so retries are idempotent. DELETE carries
// `If-Match: <row_version>` for INV-7 optimistic concurrency; a
// mismatch is mapped to VersionConflictError so the page can open
// a refetch / discard dialog.
//
// The create mutation's response carries `secret_value` exactly
// once (INV-5). The hook DOES NOT cache the `secret_value` -- the
// caller (SecretFormDialog) must render it in a one-time copy
// affordance and destroy it on dialog close.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTenant } from "@/tenant/TenantProvider";
import { ApiError } from "@/definitions/types";
import { apiFetch } from "@/api";
import type {
  CreateSecretRequest,
  CreateSecretResponse,
  SecretImpactReport,
  SecretFetchError,
  StagingOnlyError,
  VersionConflictError,
  SecretMeta,
  PromotionReviewSummary,
} from "./types";

/**
 * Cache keys for the secret reference admin editor. tenantSlug is
 * the 2nd segment (INV-1). Mirrors the FR-UI-ADM-1 adminKeys shape
 * and the FR-UI-ADM-6 webhookEndpointKeys shape; co-located here so
 * the secrets surface is self-contained.
 */
export const secretKeys = {
  secrets: (tenantSlug: string) => ["admin", tenantSlug, "secrets"] as const,
  pendingReviews: (tenantSlug: string) =>
    ["admin", tenantSlug, "pending-reviews", "secret_ref"] as const,
};

interface ListResponse {
  secrets: SecretMeta[];
}

/**
 * useSecrets -- GET /api/admin/secrets
 *
 * Returns the typed union `SecretFetchError` on failure; the page
 * maps each kind to its dedicated error component. INV-5 contract:
 * the response MUST NOT carry a `secret_value` field; the
 * server-side `containsSecretValueField` walker enforces this.
 */
export function useSecrets() {
  const { tenantSlug } = useTenant();
  return useQuery<ListResponse, ApiError>({
    queryKey: secretKeys.secrets(tenantSlug),
    queryFn: async () => {
      const r = await apiFetch("/api/admin/secrets");
      if (!r.ok) {
        throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      }
      return (await r.json()) as ListResponse;
    },
  });
}

/**
 * useCreateSecret -- POST /api/admin/secrets.
 *
 * Sends `If-Match: 1` (POST always bumps from version=1) and
 * `X-Idempotency-Key: crypto.randomUUID()`. On 201 returns the
 * CreateSecretResponse (which carries `secret_value` ONCE --
 * INV-5). On 409 with SECRET_VERSION_MISMATCH throws
 * VersionConflictError. On 403 with ENV_PRODUCTION throws
 * StagingOnlyError.
 */
export function useCreateSecret() {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();
  return useMutation<
    CreateSecretResponse,
    Error | VersionConflictError | StagingOnlyError,
    CreateSecretRequest
  >({
    mutationFn: async (req) => {
      const res = await apiFetch("/api/admin/secrets", {
        method: "POST",
        init: {
          headers: {
            "Content-Type": "application/json",
            "If-Match": "1",
          },
          body: JSON.stringify(req),
        },
        idempotencyKey: true,
      });
      if (res.status === 403) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
        };
        if (body.code === "ENV_PRODUCTION") {
          const err: StagingOnlyError = {
            kind: "staging_only",
            message:
              body.error ?? "secret reference management is staging-only",
          };
          throw err;
        }
      }
      if (res.status === 409) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
          current_version?: number;
          server_secret?: SecretMeta;
        };
        if (body.code === "SECRET_VERSION_MISMATCH") {
          const err: VersionConflictError = {
            error: "version_conflict",
            code: "SECRET_VERSION_MISMATCH",
            current_version: body.current_version ?? 0,
            server_secret: body.server_secret,
          };
          throw err;
        }
      }
      if (!res.ok) {
        throw new ApiError(res.statusText, res.status, `http_${res.status}`);
      }
      return (await res.json()) as CreateSecretResponse;
    },
    onSuccess: () => {
      // Refresh the secret list (the new ref shows up after promotion).
      void queryClient.invalidateQueries({
        queryKey: secretKeys.secrets(tenantSlug),
      });
      // BA contract (uat-ba note_13124): disarm after success.
    },
    onError: () => {
      // BA contract (uat-ba note_13124): disarm on failure.
    },
    onSettled: () => {
      // Belt-and-braces; both branches above already disarm.
    },
  });
}

/**
 * useDeleteSecret -- DELETE /api/admin/secrets/{id}.
 *
 * Carries `If-Match: <row_version>` for INV-7 optimistic concurrency.
 * On 409 SECRET_VERSION_MISMATCH throws VersionConflictError. On
 * 403 ENV_PRODUCTION throws StagingOnlyError. On 200 invalidates
 * the secret list cache.
 *
 * NOTE: callers MUST first run `useSecretImpact(secretId)` to fetch
 * the impact report and surface it in the dialog BEFORE invoking
 * this mutation. The mutation does NOT auto-fetch the impact.
 */
export function useDeleteSecret() {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();
  return useMutation<
    { deleted: true; id: string },
    Error | VersionConflictError | StagingOnlyError,
    { id: string; rowVersion: number }
  >({
    mutationFn: async ({ id, rowVersion }) => {
      const res = await apiFetch(
        `/api/admin/secrets/${encodeURIComponent(id)}`,
        {
          method: "DELETE",
          init: {
            headers: { "If-Match": String(rowVersion) },
          },
          idempotencyKey: true,
        },
      );
      if (res.status === 403) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
        };
        if (body.code === "ENV_PRODUCTION") {
          const err: StagingOnlyError = {
            kind: "staging_only",
            message:
              body.error ?? "secret reference management is staging-only",
          };
          throw err;
        }
      }
      if (res.status === 409) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
          current_version?: number;
          server_secret?: SecretMeta;
        };
        if (body.code === "SECRET_VERSION_MISMATCH") {
          const err: VersionConflictError = {
            error: "version_conflict",
            code: "SECRET_VERSION_MISMATCH",
            current_version: body.current_version ?? 0,
            server_secret: body.server_secret,
          };
          throw err;
        }
      }
      if (!res.ok) {
        throw new ApiError(res.statusText, res.status, `http_${res.status}`);
      }
      return (await res.json()) as { deleted: true; id: string };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: secretKeys.secrets(tenantSlug),
      });
      // BA contract (uat-ba note_13124): disarm after success.
    },
    onError: () => {
      // BA contract (uat-ba note_13124): disarm on failure.
    },
    onSettled: () => {
      // Belt-and-braces; both branches above already disarm.
    },
  });
}

/**
 * useSecretImpact -- GET /api/admin/secrets/{id}/impact.
 *
 * Returns the impact report listing all effect configurations
 * (http_endpoints + email_channels) that reference the secret.
 * Empty `references` array means safe to delete.
 *
 * The query is enabled only when `id` is non-empty (FR-UI-CORE-10
 * fetch state machine). The query key includes the id so deleting
 * and re-creating a secret with the same id does NOT leak the
 * previous tenant's impact report.
 */
export function useSecretImpact(id: string | null) {
  const { tenantSlug } = useTenant();
  return useQuery<SecretImpactReport, ApiError>({
    queryKey: id
      ? (["admin", tenantSlug, "secrets", id, "impact"] as const)
      : (["admin", tenantSlug, "secrets", "noop"] as const),
    enabled: Boolean(id),
    queryFn: async () => {
      const r = await apiFetch(
        `/api/admin/secrets/${encodeURIComponent(id as string)}/impact`,
      );
      if (!r.ok) {
        throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      }
      return (await r.json()) as SecretImpactReport;
    },
  });
}

/**
 * usePromotionPendingCount -- FR-PROM-3 pending-review banner hook.
 * Mirrors the FR-UI-ADM-1 / FR-UI-ADM-6 / FR-UI-ADM-8 pattern.
 */
export function usePromotionPendingCount() {
  const { tenantSlug } = useTenant();
  return useQuery<PromotionReviewSummary, ApiError>({
    queryKey: secretKeys.pendingReviews(tenantSlug),
    queryFn: async () => {
      const r = await apiFetch("/api/promotion-reviews?def_type=secret_ref");
      if (!r.ok) {
        throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      }
      return (await r.json()) as PromotionReviewSummary;
    },
  });
}

/**
 * Re-exported helper so pages can classify a fetch error into the
 * SecretFetchError union without importing the discriminator logic
 * twice.
 */
export function classifySecretFetchError(
  err: Error | ApiError | null | undefined,
): SecretFetchError {
  if (err === null || err === undefined) {
    return {
      kind: "fetch_failure",
      message: "unknown error",
      retriable: true,
    };
  }
  if (err instanceof ApiError) {
    if (err.status === 401 || err.status === 403) {
      return {
        kind: "permission_denied",
        message: err.message,
        retriable: false,
      };
    }
    if (err.status === 429) {
      return {
        kind: "rate_limit",
        message: err.message,
        retriable: true,
      };
    }
    return {
      kind: "fetch_failure",
      message: err.message,
      retriable: true,
    };
  }
  return {
    kind: "fetch_failure",
    message: err.message ?? "unknown error",
    retriable: true,
  };
}
